/**
 * Shared warehouse selection for initializing WMS document drafts.
 *
 * Every direct creation flow uses the preferred warehouse when it is available,
 * otherwise it falls back to the first active warehouse that is not blocked by
 * an inventory count.
 */
import { useQuery } from "@tanstack/react-query"
import { useCallback, useMemo } from "react"
import { WareHouseService } from "../../../client"
import { useWarehouseInventoryBlocks } from "../../../hooks/useWarehouseInventoryBlocks"
import { isOperationalDocumentWarehouse } from "./documentWarehouses"

/** Loads warehouses and exposes the common draft-bootstrap selection policy. */
export function useDocumentWarehouseBootstrap() {
  const warehousesQuery = useQuery({
    queryKey: ["wms", "warehouses", "list"],
    queryFn: () => WareHouseService.getAllWarehouses(),
  })
  const blocksQuery = useWarehouseInventoryBlocks()
  const warehouses = warehousesQuery.data ?? []

  const blockedWarehouseIds = useMemo(
    () => blocksQuery.blockedWarehouseIds,
    [blocksQuery.blockedWarehouseIds],
  )
  const availableWarehouses = useMemo(
    () =>
      warehouses.filter(
        (warehouse) =>
          warehouse.is_active &&
          isOperationalDocumentWarehouse(warehouse) &&
          !blockedWarehouseIds.has(warehouse.id),
      ),
    [blockedWarehouseIds, warehouses],
  )

  /** Resolves a preferred warehouse or the first warehouse from the shared list. */
  const resolveWarehouseId = useCallback(
    (preferredWarehouseId?: string) =>
      preferredWarehouseId &&
      availableWarehouses.some(
        (warehouse) => warehouse.id === preferredWarehouseId,
      )
        ? preferredWarehouseId
        : availableWarehouses[0]?.id,
    [availableWarehouses],
  )

  /** Reports whether a preferred warehouse is blocked by active inventory. */
  const isWarehouseBlocked = useCallback(
    (warehouseId?: string) =>
      Boolean(warehouseId && blockedWarehouseIds.has(warehouseId)),
    [blockedWarehouseIds],
  )

  const refreshAvailability = useCallback(async () => {
    const [warehouseResult, blocksResult] = await Promise.all([
      warehousesQuery.refetch(),
      blocksQuery.refetch(),
    ])
    if (warehouseResult.isError || blocksResult.isError) {
      return { ready: false as const }
    }
    const freshWarehouses = warehouseResult.data ?? []
    const freshBlocked = new Set(blocksResult.data?.warehouse_ids ?? [])
    const available = freshWarehouses.filter(
      (warehouse) =>
        warehouse.is_active &&
        isOperationalDocumentWarehouse(warehouse) &&
        !freshBlocked.has(warehouse.id),
    )
    return {
      ready: true,
      availableWarehouses: available,
      isBlocked: (warehouseId?: string) =>
        Boolean(warehouseId && freshBlocked.has(warehouseId)),
    }
  }, [blocksQuery, warehousesQuery])

  return {
    availableWarehouses,
    isPending: warehousesQuery.isPending || blocksQuery.isPending,
    isError: warehousesQuery.isError || blocksQuery.isError,
    refreshAvailability,
    resolveWarehouseId,
    isWarehouseBlocked,
  }
}
